// pannellum-react was built and packaged locally from source to get the latest
// version from github, without depending on that github repository in our
// package.json.
//
// Steps used to build and package:
//  - Clone pannellum-react from github.
//  - Navigate to pannellum-react.
//  - `npm install --legacy-peer-deps`
//  - `npm run build`
//  - `npm pack`
//  - Copy the package file to "this projekt root/local_packages/".
//  - `npm install ./local_packages/pannellum-react-1.3.6.tgz`
import { Pannellum } from 'pannellum-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { SvgCross } from '../../../components/SvgImports'
import { Size2d } from '../../../types/Size2d'
import {
  getSessionInitDataState,
  getStartupData,
  getUserSelectionTimestamp,
  setShowPanoramaViewer,
} from '../../../store/sessionDataSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useClient } from '../../../utils/useClient'
import { getTruckInfoState } from '../../../store/truckInfoSlice'
import { TdsMessage, TdsSpinner } from '@scania/tegel-react'
import { IMAGE_MESSAGE_TRANSITION_MS } from './MainImage'
import { TextId, useTextsV2 } from '../../../utils/useTexts'
import { getMarketSettingsState } from '../../../store/appSlice'

interface PanoramaViewerRootProps {
  $opacity: number
}

const PanoramaViewerRoot = styled.div<PanoramaViewerRootProps>`
  // TODO: Move this element to the root App.tsx and see if that affects
  // performance when clicking and dragging the mouse cursor.
  position: fixed;

  opacity: ${(props) => props.$opacity};

  // TODO: Transition this to semi transparent of the loading takes a long time.
  background-color: rgba(255, 255, 255, 0);

  transition-property: opacity;
  transition-duration: 300ms;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`

interface SpinnerWrapperProps {
  $opacity: number
}

const SpinnerWrapper = styled.div<SpinnerWrapperProps>`
  position: absolute;
  top: 0;
  left: 0;
  transition-property: opacity;
  transition-duration: 300ms;
  opacity: ${(props) => props.$opacity};
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  & > svg {
    width: 64px;
    height: 64px;
    color: var(--tds-blue-600);
  }
`

// TODO: Modify panellum-react and put this button inside the fullscreen
// container? It's currenly not visible in fullscreen mode.
const CrossIcon = styled.div`
  z-index: 10000;
  position: absolute;
  border-radius: 3px; // Match the built-in controls.
  background-color: white;
  color: var(--tds-grey-800);
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  cursor: pointer;
`

interface PannellumWrapperProps {
  $opacity: number
}

const PannellumWrapper = styled.div<PannellumWrapperProps>`
  width: 100%;
  height: 100%;
  transition-property: opacity;
  transition-duration: 300ms;
  opacity: ${(props) => props.$opacity};
`

const MessageWrapper = styled.div`
  position: absolute;
  cursor: default;
  left: 48px;
  top: 8px;
  right: 56px;
  display: flex;
  justify-content: center;
  transition: opacity ${IMAGE_MESSAGE_TRANSITION_MS}ms ease-out;
`

interface FovLimits {
  maxHorizontalFov: number
  minHorizontalFov: number
}

// Values that seem to look good on various screens: BEGIN
const MAX_HFOV_ARBITRARY_LIMIT = 115
const MIN_HFOV_ARBITRARY_FACTOR = 0.75
const SQUARE_VIEWPORT_ARBITRARY_MAX_HFOV = 80
// Values that seem to look good on various screens: END

export type PanoramaImageBuilder = () => Promise<{ imageUrl: string }>

export interface PanoramaViewerProps {
  buildImage: PanoramaImageBuilder
}

export const PanoramaViewer = (): JSX.Element => {
  const dispatch = useAppDispatch()
  const client = useClient()
  const startupData = useAppSelector(getStartupData)
  const sessionInitData = useAppSelector(getSessionInitDataState)
  const marketSettings = useAppSelector(getMarketSettingsState)
  const truckInfo = useAppSelector(getTruckInfoState)
  const userSelectionTimestamp = useAppSelector(getUserSelectionTimestamp)
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined)
  const [size, setSize] = useState<Size2d | null>(null)
  const [fovLimits, setFovLimits] = useState<FovLimits | null>(null)
  const [showSpinner, setShowSpinner] = useState(true)
  const [spinnerOpacity, setSpinnerOpacity] = useState(1)
  const [isClosing, setIsClosing] = useState(false)
  const [rootOpacity, setRootOpacity] = useState(0)
  const [pannellumWrapperOpacity, setPannellumWrapperOpacity] = useState(0)
  const panoramaRootRef = useRef<HTMLDivElement>(null)
  const [messageShow, setMessageShow] = useState<boolean>(true)
  const [messageVisible, setMessageVisible] = useState<boolean>(true)
  const t = useTextsV2()

  // #########################################################################
  // #             Configuration change update state: BEGIN                  #
  // #########################################################################

  // Helps set nullable callbacks with setState.
  interface ConfigDependentImageParams {
    // Is expected to change on configuration changes, which in turn should
    // trigger image updates in the image component.
    buildPanoramaImage: PanoramaImageBuilder | null
  }

  // TODO: Store all configuratin state update on a single object using a single
  // useState.
  //
  // TODO: Store together with other configuration state data.
  const [configDependentImageParams, setConfigDependentImageParams] =
    useState<ConfigDependentImageParams>({
      buildPanoramaImage: null,
    })

  // #########################################################################
  // #             Configuration change update state: END                    #
  // #########################################################################

  // Rebind the image builder callback on changes that may affect the resulting
  // images and in turn triggering image generation.
  useEffect(() => {
    if (!startupData) {
      return
    }
    if (!sessionInitData) {
      return
    }
    if (!client) {
      //setConfigDependentImageBuilder({ buildFlexImage: null, buildPanoramaImage: null })
      return
    }
    const newImageBuilders: ConfigDependentImageParams = {
      buildPanoramaImage: async () => {
        if (startupData.panoramaImageWidth == null) {
          throw new Error('Expected panoramaImageWidth to be non-null.')
        }
        const scaledWidth = startupData.panoramaImageWidth
        const scaledHeight = 0
        const seriesName = '360'
        const frame = 1
        const cropRatioX = 0.5
        const cropRatioY = 0.5
        const croppedWidth = 0
        const croppedHeight = 0
        const fileType = 'jpg'
        const useHd = false
        const autoCrop = false
        const skip = null
        const skipGroups = null
        const onlyUse = null
        const onlyUseGroups = null
        const bgColorHexArgb = 'FFFFFFFF'
        const allowPadding = false
        const domainRelativeUrl = (
          await client.getCurrentConfigImage({
            sessionId: sessionInitData.sessionId,
            scaledWidth,
            scaledHeight,
            seriesName,
            frame,
            cropRatioX,
            cropRatioY,
            croppedWidth,
            croppedHeight,
            fileType,
            useHd,
            autoCrop,
            skip,
            skipGroups,
            onlyUse,
            onlyUseGroups,
            bgColorHexArgb,
            allowPadding,
          })
        ).getCurrentConfigImage
        if (!domainRelativeUrl) {
          throw new Error('Expected domainRelativeUrl to be defined.')
        }
        return { imageUrl: domainRelativeUrl }
      },
    }
    setConfigDependentImageParams(newImageBuilders)
  }, [client, sessionInitData, startupData, truckInfo, userSelectionTimestamp])

  useEffect(() => {
    const asyncWrapper = async () => {
      if (!configDependentImageParams.buildPanoramaImage) {
        return
      }
      const response = await configDependentImageParams.buildPanoramaImage()
      setImageUrl(response.imageUrl)
    }
    asyncWrapper()
  }, [configDependentImageParams])

  const handleKeydown = useCallback(
    (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        setIsClosing(true)
      }
    },
    [setIsClosing],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeydown)
    return () => {
      document.removeEventListener('keydown', handleKeydown)
    }
  }, [handleKeydown])

  const resizeObserver = useRef(
    new ResizeObserver((entries) => {
      const target = entries[0].target
      const size: Size2d = {
        width: target.clientWidth,
        height: target.clientHeight,
      }
      setSize(size)
    }),
  )

  useEffect(() => {
    if (!panoramaRootRef.current) {
      return
    }
    const observer = resizeObserver
    observer.current.observe(panoramaRootRef.current)
    return () => {
      observer.current.disconnect()
    }
  }, [panoramaRootRef])

  useEffect(() => {
    if (!size) {
      return
    }
    if (size.height === 0) {
      return
    }
    let newHMax =
      (size.width / size.height) * SQUARE_VIEWPORT_ARBITRARY_MAX_HFOV
    newHMax = Math.min(MAX_HFOV_ARBITRARY_LIMIT, newHMax)
    const newState: FovLimits = {
      maxHorizontalFov: newHMax,
      minHorizontalFov: newHMax * MIN_HFOV_ARBITRARY_FACTOR,
    }
    setFovLimits(newState)
  }, [size])

  const handleSpinnerTransitionEnd = useCallback(() => {
    setShowSpinner(false)
  }, [setShowSpinner])

  const handleRootTransitionEnd = useCallback(() => {
    if (isClosing) {
      dispatch(setShowPanoramaViewer(false))
    }
  }, [isClosing, dispatch])

  useEffect(() => {
    setRootOpacity(isClosing ? 0 : 1)
  }, [isClosing])

  const handleCrossClick = useCallback(() => {
    setIsClosing(true)
  }, [setIsClosing])

  const handleMessageClose = () => {
    setMessageShow(false)
  }

  const handleImageMessageTransitionEnd = useCallback(() => {
    if (!messageShow) {
      setMessageVisible(false)
    }
  }, [messageShow])

  return (
    <PanoramaViewerRoot
      ref={panoramaRootRef}
      $opacity={rootOpacity}
      onTransitionEnd={handleRootTransitionEnd}
    >
      {fovLimits && (
        <PannellumWrapper $opacity={pannellumWrapperOpacity}>
          <Pannellum
            width="100%"
            height="100%"
            image={imageUrl}
            pitch={-20}
            // Focus on the driver side, TODO: Detect RHD/LHD market by market code.
            yaw={-25}
            // When the max limit changes, change the current hfov as well.
            hfov={fovLimits.maxHorizontalFov}
            minHfov={fovLimits.minHorizontalFov}
            maxHfov={fovLimits.maxHorizontalFov}
            // Deactivated as part of SC1M-3453, to avoid having the fullscreen
            // mode without the disclaimer message. It's currently not possible
            // to have elements in top of the Pannellum element when it's in
            // fullscreen mode. We probably need to tightly integrate both
            // Pannellum-React and Pannellum into our code to make that happen,
            // to give us access to the inner element that's used for switching
            // to fullscreen. OR, we could add our own fullscreen button in this
            // component. It seems to work correctly with
            // panoramaRootRef.requestFullscreen().
            showFullscreenCtrl={false}
            autoLoad
            onLoad={() => {
              setPannellumWrapperOpacity(1)
              setSpinnerOpacity(0)
            }}
            onError={() => {
              console.error(
                'Failed to load panorama image! This can happen for any loading error, network errors, WebGL errors, etc.',
              )
              setIsClosing(true)
            }}
          />
          {messageVisible &&
            marketSettings?.cabUpdateDisclaimersEnabled === true && (
              <MessageWrapper
                onTransitionEnd={handleImageMessageTransitionEnd}
                onClick={handleMessageClose}
                onTouchEnd={handleMessageClose}
                style={{
                  opacity: messageShow ? 1 : 0,
                }}
              >
                <TdsMessage
                  variant="information"
                  header={t(TextId.DESIGN_UPDATE_DISCLAIMER_HEADER)} // Design update in progress
                >
                  {
                    t(TextId.DESIGN_UPDATE_DISCLAIMER_TEXT) // The interior design shown may be outdated due to ongoing product
                  }
                </TdsMessage>
              </MessageWrapper>
            )}
          <CrossIcon onClick={handleCrossClick}>
            <SvgCross />
          </CrossIcon>
        </PannellumWrapper>
      )}

      {
        // TODO: Move the spinner to App.tsx?
      }
      {showSpinner && (
        <SpinnerWrapper
          $opacity={spinnerOpacity}
          onTransitionEnd={handleSpinnerTransitionEnd}
        >
          <TdsSpinner size="md" />
        </SpinnerWrapper>
      )}
    </PanoramaViewerRoot>
  )
}
